import { type FC, useMemo } from 'react'
import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import { type DehydratedState, dehydrate, QueryClient } from '@tanstack/react-query'

import { CatalogView } from 'views/Catalog'
import type { CatalogFilteringState } from 'views/Catalog/LandingPage'

import {
  type Category,
  createNonEmptyCategoriesQuery,
  createGroupedIntegrationsQuery,
} from '@segment/api/routes/catalog/queries'
import { createClient } from '@segment/api/algolia/client'

import { CatalogClientProvider, createCatalogIndex } from '@segment/contexts/catalogIndex'
import { createStaticSiteBackend, StaticSiteBackendProvider } from '@segment/contexts/staticSiteBackend'

import type { AppExternalPageProps } from '@segment/next-shared/Providers'

export interface CatalogProps {
  filteringState: CatalogFilteringState
  categoryId: string | null
  categories: Category[]
}

const Catalog: FC<InferGetStaticPropsType<typeof getStaticProps>> = ({ filteringState, categoryId, categories }) => {
  const catalogClient = useMemo(() => createClient(), [])
  const staticSiteBackend = useMemo(() => createStaticSiteBackend(), [])

  return (
    <StaticSiteBackendProvider value={staticSiteBackend}>
      <CatalogClientProvider value={catalogClient}>
        <CatalogView filteringState={filteringState} categoryId={categoryId} categories={categories} />
      </CatalogClientProvider>
    </StaticSiteBackendProvider>
  )
}

export const getStaticProps: GetStaticProps<AppExternalPageProps & CatalogProps> = async () => {
  const queryClient = new QueryClient()
  const algoliaClient = createClient()
  const catalogIndex = createCatalogIndex(algoliaClient)
  const staticSiteBackend = createStaticSiteBackend()
  const query = ``
  const destination = true
  const source = true

  const categories = await queryClient.fetchQuery(
    ...createNonEmptyCategoriesQuery({ catalogIndex, staticSiteBackend }),
    { staleTime: Infinity }
  )

  await queryClient.prefetchInfiniteQuery(
    ...createGroupedIntegrationsQuery(catalogIndex, {
      query,
      destination,
      source,
    }),
    { staleTime: Infinity }
  )

  const dangerousDehydratedState = dehydrate(queryClient)

  const safeDehydratedState: DehydratedState = JSON.parse(JSON.stringify(dangerousDehydratedState))

  const props: AppExternalPageProps & CatalogProps = {
    dehydratedState: safeDehydratedState,
    categoryId: `all`,
    categories,
    filteringState: {
      query,
      destination,
      source,
    },
  }

  return {
    props,
  }
}

export default Catalog
